<script lang="ts" setup>
import { computed, toRefs, watch, ref, onMounted } from 'vue';
import InputNumber from 'primevue/inputnumber';
import RadioButton from 'primevue/radiobutton';
import ClinicianDisciplineIcon from './ClinicianDisciplineIcon.vue';
import OrganizationContractRemoveServiceProviderModal from './OrganizationContractRemoveServiceProviderModal.vue';
import { ClinicianContractDetails } from '@/types';
import { CONTRACT_ENUMS } from '@/enum';
import { useDateTime } from '@/composables';
import { useOrganizationContractsStore } from '@/store';

interface Props {
  clinician: ClinicianContractDetails;
  justAdded?: boolean;
}

const props = defineProps<Props>();
const { clinician, justAdded } = toRefs(props);
const contractStore = useOrganizationContractsStore();
const loading = ref(true);
const removeServiceProviderModalOpen = ref(false);
const canEdit = contractStore.canEditOrCreateContracts;
const editType = contractStore.editType;
const hasNoEventsScheduled = ref(false);

onMounted(async () => {
  if (justAdded.value || editType !== CONTRACT_ENUMS.CONTRACT_EDIT_TYPES.EDIT) {
    loading.value = false;
    return;
  }
  await checkClinicianEventCount();
});

const checkClinicianEventCount = async () => {
  const response = await contractStore
    .getServiceProviderEventCount(clinician.value.id as number)
    .catch(() => (loading.value = false));
  if (response === 0) hasNoEventsScheduled.value = true;
  loading.value = false;
};

const hourlyRate = computed<number>({
  get() {
    return !clinician.value.rate_is_hourly ? 0 : clinician.value.rate;
  },
  set(value) {
    clinician.value.rate = value;
  },
});

const salaryRate = computed<number>({
  get() {
    return clinician.value.rate_is_hourly ? 0 : clinician.value.rate;
  },
  set(value) {
    clinician.value.rate = value;
  },
});

const removedFromContractDate = computed<string>(() => {
  if (clinician.value.document_by_date) {
    const documentByDate = new Date(clinician.value.document_by_date);
    const followingMonth = ((documentByDate.getMonth() + 1) % 12) + 1;
    const yearValue = followingMonth !== 1 ? documentByDate.getFullYear() : documentByDate.getFullYear() + 1;

    return `${followingMonth}/01/${yearValue}`;
  }
  return '';
});

const limitToCreateEvents = computed<string>(() => {
  if (clinician.value.schedule_by_date) {
    const scheduleByDate = new Date(clinician.value.schedule_by_date);
    const month = scheduleByDate.getMonth() + 1;
    const day = scheduleByDate.getDate();
    const year = scheduleByDate.getFullYear();

    return `${month}/${day}/${year}`;
  }
  return '';
});

const documentByDate = computed<string>(() => {
  if (clinician.value.document_by_date) {
    const documentByDate = new Date(clinician.value.document_by_date);

    const month = documentByDate.getMonth() + 1;
    const day = documentByDate.getDate();
    const year = documentByDate.getFullYear();

    return `${month}/${day}/${year}`;
  }
  return '';
});

//KEY for discipline code/icon name
const icons = [
  { discipline_name: 'Behavioral Intervention', code: 'BI', name: 'emoji_objects' },
  { discipline_name: 'Occupational Therapy', code: 'OT', name: 'work' },
  { discipline_name: 'Physical Therapy', code: 'PT', name: 'emoji_people' },
  { discipline_name: 'Social Work/Counseling', code: 'SWC', name: 'emoji_people' },
  { discipline_name: 'Psychology', code: 'PSY', name: 'psychology' },
  { discipline_name: 'Speech And Language', code: 'SL', name: 'record_voice_over' },
];

/**
 * find the icon that goes with the discipline
 */
const getObjectByCodeOrName = (code: string) =>
  icons.find((icon) => icon.code === code || icon.discipline_name === code);

watch(
  clinician,
  () => {
    clinician.value.updated = true;
    clinician.value.person_id = clinician.value.person?.id;
  },
  { deep: true },
);

/**
 * delete the clinician
 */
const onRemove = async (endDates: Record<string, Date>) => {
  toggleConfirmRemoveServiceProviderModal();
  const formattedEndDates = {
    schedule_by_date: useDateTime().formatDateForApi(endDates.endDate),
    document_by_date: useDateTime().formatDateForApi(endDates.serviceEndDate),
  };
  await contractStore.deleteServiceProvider(clinician.value.id as number, formattedEndDates, false);
  clinician.value.document_by_date = formattedEndDates.document_by_date;
  clinician.value.schedule_by_date = formattedEndDates.schedule_by_date;
  contractStore.doToast('Service Provider has been removed', 'Removed', false);
};

const toggleConfirmRemoveServiceProviderModal = async () => {
  const preventDateSelectionModal =
    justAdded.value || !contractStore.selectedContractStarted || hasNoEventsScheduled.value;
  if (preventDateSelectionModal) {
    // if the clinician being removed was only temporarily on the contract before it was saved
    // we follow this flow to prevent the confirm modal from showing
    await contractStore.deleteServiceProvider(
      clinician.value.id as number,
      { schedule_by_date: null, document_by_date: null },
      true,
    );
    contractStore.doToast('Service Provider has been removed', 'Removed', false);
    return;
  }

  removeServiceProviderModalOpen.value = !removeServiceProviderModalOpen.value;
};

const undoServiceProviderRemoval = async () => {
  await contractStore.restoreServiceProvider(clinician.value.id as number);
  clinician.value.document_by_date = null;
  clinician.value.schedule_by_date = null;
  contractStore.doToast('Service Provider will no longer be removed from this contract after save', 'Success', false);
};
</script>

<template>
  <div class="clinician-panel w-full col-12 mt-3 p-3 border-round-md bg-gray-100">
    <div class="inline-flex w-full pt-0 pb-3">
      <div class="flex flex-wrap align-content-center mr-2">
        <span class="text-sm font-semibold" v-text="clinician.person?.display_name" />
      </div>
      <div class="disciplines inline-flex align-content-center flex-wrap">
        <ClinicianDisciplineIcon
          v-for="(discipline, key) in clinician.person?.clinician_disciplines"
          :key="key"
          :icon="getObjectByCodeOrName(discipline.code || discipline.name)"
          class-name="inline-flex px-2"
        />
      </div>
      <div class="flex flex-column flex-wrap align-content-center justify-content-end ml-auto">
        <button
          v-if="!clinician.schedule_by_date && !clinician.document_by_date && canEdit && !loading"
          class="p-link justify-content-end"
          @click="toggleConfirmRemoveServiceProviderModal"
        >
          <Icon name="delete" size="text-xl" class="text-600" />
        </button>
        <template v-else-if="canEdit && !loading">
          <span class="pb-2 flex justify-content-end">
            Will no longer appear on this contract after
            <b class="pl-1">{{ removedFromContractDate }}</b>
          </span>
          <span class="pb-2 flex justify-content-end">
            Cannot create events after
            <b class="pl-1">{{ limitToCreateEvents }}</b>
          </span>
          <span class="flex justify-content-end">
            Cannot edit or document events after
            <b class="pl-1">{{ documentByDate }}</b>
          </span>
          <button class="p-link flex ml-auto pt-2 align-items-center" @click="undoServiceProviderRemoval">
            <Icon name="undo" size="text-xl" class="text-primary mt-1" />
            <span class="p-button-label text-primary ml-1"><b>Undo Remove</b></span>
          </button>
        </template>
      </div>
    </div>

    <div class="inline-flex w-full pt-0">
      <div class="col p-3 border-round-left-md bg-white border-right-1 border-gray-100 bg-white">
        <div class="field">
          <label for="hours_contracted">Hours Contracted</label>
          <InputNumber
            id="hours_contracted"
            v-model="clinician.hours"
            class="data w-full"
            :disabled="!canEdit"
            mode="decimal"
            :max-fraction-digits="2"
            :min-fraction-digits="0"
          />
        </div>
        <!-- TODO: there is no variable for this in the contract from the API.  -->
        <!-- <div class="field">
          <div class="flex justify-content-between pt-4 pr-2 pb-4 pl-2">
            <label for="unlimited" class="mt-1">Allow Unlimited Group Events</label>
            <InputSwitch id="unlimited" v-model="mock.allow_unlimited" />
          </div>
        </div> -->
      </div>

      <div class="col p-3 border-round-right-md bg-white border-left-1 border-gray-100 bg-white">
        <div class="field mb-0">
          <label for="no_show">Compensation Type</label>
          <div class="flex mb-0">
            <div class="col p-0 field-radiobutton">
              <RadioButton
                v-model="clinician.rate_is_hourly"
                :value="1"
                input-id="hourly"
                name="payment_type"
                input-class="mb-0"
                class="relative"
                :disabled="!canEdit"
              />
              <label for="hourly">Hourly</label>
            </div>
            <div class="col p-0 inline-flex">
              <InputNumber
                v-model="hourlyRate"
                placeholder="$0"
                mode="currency"
                currency="USD"
                locale="en-US"
                class="p-inputtext-sm ml-auto"
                :input-style="{ height: '1.5rem', width: '7.25rem' }"
                :disabled="clinician.rate_is_hourly === 0 || !canEdit"
                @focus="(e: Event) => e?.target?.select()"
              />
            </div>
          </div>
          <div class="flex">
            <div class="col p-0 field-radiobutton mb-0">
              <RadioButton
                v-model="clinician.rate_is_hourly"
                :value="0"
                input-id="salary"
                name="payment_type"
                input-class="mb-0"
                class="relative"
                :disabled="!canEdit"
              />
              <label for="salary">Salary</label>
            </div>
            <div class="col p-0 inline-flex">
              <InputNumber
                v-model="salaryRate"
                :disabled="clinician.rate_is_hourly === 1 || !canEdit"
                placeholder="$0"
                mode="currency"
                currency="USD"
                locale="en-US"
                class="p-inputtext-sm ml-auto"
                :min="0"
                :input-style="{ height: '1.5rem', width: '7.25rem' }"
                @focus="(e: Event) => e?.target?.select()"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <OrganizationContractRemoveServiceProviderModal
      v-if="removeServiceProviderModalOpen"
      :clinician="clinician"
      @remove="onRemove"
      @remove-cancel="toggleConfirmRemoveServiceProviderModal"
    />
  </div>
</template>

<style scoped lang="scss">
.disciplines {
  div {
    border-left: 2px solid $grayMedium;

    span {
      position: relative;
      top: -2px;
    }
  }
}
</style>
